/**
 * Demo Showcase Projects - Designed for Video Presentation
 * 
 * These are the most impressive and visually appealing projects
 * specifically curated for the 3-minute demo video.
 */

import { Project, AIModel } from '../types/user';

// Showcase Projects - The Stars of the Demo
export const showcaseProjects: Project[] = [
  // Child Project - Emma's Voice Pet (8 years old)
  {
    id: 'showcase-emma-voice-pet',
    name: '🐕 My Smart Puppy Friend',
    description: 'A magical puppy that listens to my voice and does tricks! I taught it to sit, roll over, and even dance!',
    isPublic: true,
    tags: ['voice-ai', 'pets', 'kids', 'interactive'],
    createdAt: new Date('2024-12-14'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'child-emma-8',
    content: {
      type: 'voice-interactive',
      commands: ['sit', 'roll over', 'dance', 'speak', 'play fetch'],
      animations: ['happy_tail_wag', 'sitting', 'rolling', 'dancing', 'barking'],
      aiModel: 'emma-puppy-voice-model',
      accuracy: 92,
      trainingTime: '15 minutes'
    },
    showcase: {
      videoTimestamp: '0:30-0:50',
      highlights: [
        'Child-safe AI training interface',
        'Voice command recognition',
        'Interactive pet responses',
        'Parental approval system'
      ],
      demoScript: 'Watch Emma train her AI puppy using just her voice!'
    }
  },

  // Student Project - Maya's AI Study Buddy (14 years old)
  {
    id: 'showcase-maya-study-buddy',
    name: '🤖 StudyBot - My AI Tutor',
    description: 'An AI assistant that helps with homework, explains concepts, and even creates custom quizzes based on my learning style!',
    isPublic: true,
    tags: ['ai-tutor', 'education', 'nlp', 'personalized-learning'],
    createdAt: new Date('2024-12-12'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'student-maya-14',
    content: {
      type: 'ai-chatbot',
      capabilities: [
        'Subject Q&A (Math, Science, History)',
        'Concept explanation with examples',
        'Custom quiz generation',
        'Learning style adaptation',
        'Study schedule optimization'
      ],
      aiModels: ['text-understanding', 'question-generation', 'learning-analytics'],
      subjects: ['Algebra', 'Biology', 'World History', 'Chemistry'],
      accuracy: 88,
      userRating: 4.9
    },
    showcase: {
      videoTimestamp: '0:50-1:10',
      highlights: [
        'Natural language processing',
        'Educational AI applications',
        'Adaptive learning system',
        'Student-created AI project'
      ],
      demoScript: 'Maya built an AI tutor that adapts to her learning style!'
    }
  },

  // Advanced Student Project - Jordan's Computer Vision Game (16 years old)
  {
    id: 'showcase-jordan-vision-game',
    name: '👁️ EcoScan - Environmental AI Game',
    description: 'Use your camera to identify and classify environmental objects! My AI can recognize recyclables, plants, pollution, and wildlife with 94% accuracy.',
    isPublic: true,
    tags: ['computer-vision', 'environment', 'gaming', 'machine-learning'],
    createdAt: new Date('2024-12-10'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'student-jordan-16',
    content: {
      type: 'computer-vision-game',
      categories: [
        'Recyclables (Paper, Plastic, Glass, Metal)',
        'Native Plants (25 species)',
        'Pollution Indicators',
        'Wildlife (Birds, Insects)',
        'Clean Energy Sources'
      ],
      aiModels: ['image-classification', 'object-detection', 'environmental-analysis'],
      accuracy: 94,
      trainingDataPoints: 12000,
      gamification: {
        points: 2850,
        level: 'Eco Expert',
        achievements: ['Tree Hugger', 'Recycling Champion', 'Wildlife Protector']
      }
    },
    showcase: {
      videoTimestamp: '1:10-1:35',
      highlights: [
        'Advanced computer vision',
        'Real-world problem solving',
        'High accuracy AI model',
        'Gamified learning experience'
      ],
      demoScript: 'Jordan trained an AI to help save the environment!'
    }
  },

  // Teacher Template - Mrs. Davis's Classroom AI
  {
    id: 'showcase-teacher-classroom-ai',
    name: '📚 Classroom Voice Assistant',
    description: 'An AI teaching assistant that helps students with questions, manages classroom activities, and provides instant feedback on projects.',
    isPublic: true,
    tags: ['education', 'teacher-tools', 'voice-ai', 'classroom-management'],
    createdAt: new Date('2024-12-08'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'teacher-mrs-davis',
    content: {
      type: 'educational-assistant',
      features: [
        'Student question answering',
        'Project feedback generation',
        'Classroom activity suggestions',
        'Learning progress tracking',
        'Multilingual support (Spanish, French)'
      ],
      aiModels: ['voice-recognition', 'educational-content', 'feedback-generation'],
      classroomIntegration: true,
      studentUsage: 28,
      avgImprovement: '23% increase in assignment scores'
    },
    showcase: {
      videoTimestamp: '1:35-1:55',
      highlights: [
        'Teacher productivity tools',
        'AI-powered classroom management',
        'Student engagement metrics',
        'Educational AI templates'
      ],
      demoScript: 'Teachers can create AI assistants to enhance learning!'
    }
  },

  // Creator Template - Alex's AI Art Studio
  {
    id: 'showcase-creator-ai-art-studio',
    name: '🎨 AI Creative Studio Template',
    description: 'A complete AI-powered art creation suite that students can customize. Generate art styles, blend images, and create unique digital masterpieces!',
    isPublic: true,
    tags: ['ai-art', 'creativity', 'template', 'generative-ai'],
    createdAt: new Date('2024-12-05'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'creator-alex-kim',
    content: {
      type: 'ai-art-generator',
      capabilities: [
        'Style transfer (Van Gogh, Picasso, Modern)',
        'Image-to-image translation',
        'Text-to-image generation',
        'Art history education',
        'Portfolio creation tools'
      ],
      aiModels: ['style-transfer', 'image-generation', 'art-classification'],
      templateSales: 1428,
      userRating: 4.8,
      earnings: '$12,750',
      ageRange: '10-18 years'
    },
    showcase: {
      videoTimestamp: '1:55-2:15',
      highlights: [
        'AI-powered creativity tools',
        'Template marketplace success',
        'Creator economy features',
        'Art education integration'
      ],
      demoScript: 'Creators build AI templates that inspire thousands of students!'
    }
  },

  // Parent Dashboard Highlight - Child Safety Monitoring
  {
    id: 'showcase-parent-safety-dashboard',
    name: '🛡️ AI Safety Monitor',
    description: 'Parent dashboard showing real-time AI safety monitoring, content filtering, and learning progress tracking for children.',
    isPublic: false, // Parent-only view
    tags: ['safety', 'parental-controls', 'monitoring', 'child-protection'],
    createdAt: new Date('2024-12-15'),
    lastModified: new Date('2024-12-15').toISOString(),
    ownerId: 'parent-sarah-johnson',
    content: {
      type: 'safety-dashboard',
      features: [
        'Real-time AI interaction monitoring',
        'Automatic content filtering',
        'Screen time management',
        'Learning progress reports',
        'Achievement notifications'
      ],
      childActivity: {
        lastSession: '45 minutes',
        projectsCompleted: 3,
        safetyAlerts: 0,
        learningMilestones: 2
      },
      safetyMetrics: {
        contentFiltered: 12,
        appropriateInteractions: '100%',
        parentApprovalRequired: 2
      }
    },
    showcase: {
      videoTimestamp: '2:15-2:35',
      highlights: [
        'Child safety first approach',
        'Transparent AI monitoring',
        'Parent-child collaboration',
        'Age-appropriate AI experiences'
      ],
      demoScript: 'Parents have complete visibility and control over AI interactions!'
    }
  }
];

// Showcase AI Models with impressive metrics
export const showcaseAIModels: AIModel[] = [
  {
    id: 'emma-puppy-voice-model',
    name: 'Emma\'s Puppy Voice Commands',
    type: 'voice',
    createdAt: new Date('2024-12-14').toISOString(),
    trainingData: [],
    modelData: {
      commands: ['sit', 'roll over', 'dance', 'speak', 'play fetch'],
      accuracy: 0.92,
      trainingSamples: 156,
      trainingTime: 15, // minutes
      childFriendly: true,
      safetyChecked: true,
      authorId: 'child-emma-8'
    }
  },
  {
    id: 'maya-study-nlp-model',
    name: 'StudyBot Natural Language Model',
    type: 'text',
    createdAt: new Date('2024-12-12').toISOString(),
    trainingData: [],
    modelData: {
      subjects: ['Math', 'Science', 'History'],
      accuracy: 0.88,
      trainingSamples: 2400,
      responseTime: 0.3, // seconds
      languages: ['English', 'Spanish'],
      authorId: 'student-maya-14'
    }
  },
  {
    id: 'jordan-ecoscan-vision-model',
    name: 'EcoScan Computer Vision Model',
    type: 'image',
    createdAt: new Date('2024-12-10').toISOString(),
    trainingData: [],
    modelData: {
      categories: ['Recyclables', 'Plants', 'Pollution', 'Wildlife', 'Clean Energy'],
      accuracy: 0.94,
      trainingSamples: 12000,
      inferenceTime: 0.2, // seconds
      environmentalImpact: 'positive',
      authorId: 'student-jordan-16'
    }
  }
];

// Demo Flow Configuration for Video Recording
export const demoFlowConfig = {
  totalDuration: '3:00', // 3 minutes
  segments: [
    {
      id: 'intro',
      duration: '0:00-0:30',
      title: 'Welcome to CraftyJoy',
      description: 'AI-powered learning platform for all ages',
      userType: 'overview',
      keyFeatures: ['Safe AI', 'Age-appropriate', 'Educational', 'Creative']
    },
    {
      id: 'child-demo',
      duration: '0:30-0:50',
      title: 'Kids Create with Voice AI',
      description: 'Emma (8) trains her AI puppy',
      userType: 'child',
      project: 'showcase-emma-voice-pet',
      highlights: ['Voice training', 'Safe interactions', 'Creative play']
    },
    {
      id: 'student-demo',
      duration: '0:50-1:35',
      title: 'Students Build Advanced AI',
      description: 'Maya & Jordan create real-world AI solutions',
      userType: 'student',
      projects: ['showcase-maya-study-buddy', 'showcase-jordan-vision-game'],
      highlights: ['Advanced AI', 'Real problems', 'High accuracy']
    },
    {
      id: 'teacher-demo',
      duration: '1:35-1:55',
      title: 'Teachers Enhance Classrooms',
      description: 'Mrs. Davis uses AI teaching assistants',
      userType: 'teacher',
      project: 'showcase-teacher-classroom-ai',
      highlights: ['Classroom tools', 'Student tracking', 'Educational AI']
    },
    {
      id: 'creator-demo',
      duration: '1:55-2:15',
      title: 'Creators Build & Earn',
      description: 'Alex creates AI templates for the marketplace',
      userType: 'creator',
      project: 'showcase-creator-ai-art-studio',
      highlights: ['Template marketplace', 'Creator earnings', 'Wide reach']
    },
    {
      id: 'parent-demo',
      duration: '2:15-2:35',
      title: 'Parents Monitor Safely',
      description: 'Sarah tracks Emma\'s AI learning journey',
      userType: 'parent',
      project: 'showcase-parent-safety-dashboard',
      highlights: ['Safety monitoring', 'Progress tracking', 'Peace of mind']
    },
    {
      id: 'conclusion',
      duration: '2:35-3:00',
      title: 'CraftyJoy: Where AI Meets Learning',
      description: 'Join thousands of learners exploring AI safely',
      userType: 'overview',
      callToAction: 'Start your AI journey today at CraftyJoy.com'
    }
  ]
};

// Video Recording Helpers
export const getShowcaseProjectForSegment = (segmentId: string) => {
  const segment = demoFlowConfig.segments.find(s => s.id === segmentId);
  if (!segment || !segment.project) return null;
  
  return showcaseProjects.find(p => p.id === segment.project);
};

export const getShowcaseProjectsForSegment = (segmentId: string) => {
  const segment = demoFlowConfig.segments.find(s => s.id === segmentId);
  if (!segment || !segment.projects) return [];
  
  return segment.projects.map(projectId => 
    showcaseProjects.find(p => p.id === projectId)
  ).filter(Boolean);
};

// Demo narration scripts
export const demoNarrationScripts = {
  intro: "Meet CraftyJoy - where kids, students, teachers, and creators explore AI safely. From voice commands to computer vision, everyone can build, learn, and create with artificial intelligence.",
  
  childDemo: "Eight-year-old Emma just trained her AI puppy to respond to voice commands. Watch how our child-safe interface makes AI training as fun as playing with a real pet!",
  
  studentDemo: "Students like Maya and Jordan are solving real-world problems. Maya built an AI tutor that adapts to her learning style, while Jordan created an environmental scanner with 94% accuracy!",
  
  teacherDemo: "Teachers like Mrs. Davis are transforming their classrooms with AI assistants that help 28 students simultaneously, boosting assignment scores by 23%.",
  
  creatorDemo: "Creators like Alex build AI templates that reach thousands of students. His AI Art Studio has earned over $12,000 while inspiring creativity worldwide.",
  
  parentDemo: "Parents like Sarah have complete visibility and control. Real-time safety monitoring, content filtering, and progress tracking ensure kids learn AI responsibly.",
  
  conclusion: "CraftyJoy makes AI accessible, educational, and safe for everyone. Join thousands of learners already exploring the future of AI education."
}; 